import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';

import '../../clients/styles/form.css';

// POST /auth/change-password — { current_password, new_password }.
//
// THE ONLY WAY ANYBODY CHANGES A PASSWORD IN THIS SYSTEM. There is no route
// that sets somebody else's: PATCH /admin/users/{id} has no password field, on
// purpose. Which also means that until this screen existed, a person given a
// password at sign-up was stuck with it forever.

const PASSWORD_MIN = 10;   // data/schemas/user.js
const PASSWORD_MAX = 100;

const EMPTY = { current_password: '', new_password: '', repeat: '' };

export default function ChangePasswordForm({ onDone }) {
    const [values, setValues] = useState(EMPTY);
    const [errors, setErrors] = useState({});

    const changePassword = useMutation({
        mutationFn: (payload) => api.post('auth/change-password', payload).then(r => r.data),
    });

    const change = (field) => (e) => {
        const { value } = e.target;
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const validate = () => {
        const found = {};
        if (!values.current_password) found.current_password = 'La contraseña actual es requerida';

        if (!values.new_password) found.new_password = 'La contraseña nueva es requerida';
        else if (values.new_password.length < PASSWORD_MIN) {
            found.new_password = `Debe tener al menos ${PASSWORD_MIN} caracteres`;
        } else if (values.new_password.length > PASSWORD_MAX) {
            found.new_password = `No puede pasar de ${PASSWORD_MAX} caracteres`;
        }

        // The API takes no confirmation field. It is checked here anyway: a
        // typo in a password nobody can read back is a lockout, and this is the
        // only place to catch it.
        if (values.new_password && values.repeat !== values.new_password) {
            found.repeat = 'Las dos no coinciden';
        }
        return found;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate();
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            await changePassword.mutateAsync({
                current_password: values.current_password,
                new_password: values.new_password,
            });
            setValues(EMPTY);
            toast.success('Contraseña cambiada');
            onDone?.();
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo cambiar la contraseña');
        }
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <div className="client-form__section">
                <h3 className="client-form__section-title">Cambiar la contraseña</h3>
                <p className="client-form__section-text">
                    Solo vos podés cambiar la tuya: no hay forma de que otra persona la
                    cambie por vos, ni siquiera quien administra.
                </p>
            </div>

            <div className="client-form__grid">
                <label className="client-form__field client-form__field--wide">
                    <span className="client-form__label">
                        Contraseña actual <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.current_password ? 'client-form__input--invalid' : ''}`}
                            type="password"
                            value={values.current_password}
                            onChange={change('current_password')}
                            autoComplete="current-password"
                            disabled={changePassword.isPending}
                            required
                        />
                    </span>
                    {errors.current_password && (
                        <span className="client-form__error">{errors.current_password}</span>
                    )}
                </label>

                <label className="client-form__field">
                    <span className="client-form__label">
                        Nueva <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.new_password ? 'client-form__input--invalid' : ''}`}
                            type="password"
                            value={values.new_password}
                            onChange={change('new_password')}
                            minLength={PASSWORD_MIN}
                            maxLength={PASSWORD_MAX}
                            autoComplete="new-password"
                            disabled={changePassword.isPending}
                            required
                        />
                    </span>
                    {errors.new_password
                        ? <span className="client-form__error">{errors.new_password}</span>
                        : <span className="client-form__note">Al menos {PASSWORD_MIN} caracteres.</span>}
                </label>

                <label className="client-form__field">
                    <span className="client-form__label">
                        Repetir <span className="client-form__required">*</span>
                    </span>
                    <span className="client-form__control">
                        <input
                            className={`client-form__input ${errors.repeat ? 'client-form__input--invalid' : ''}`}
                            type="password"
                            value={values.repeat}
                            onChange={change('repeat')}
                            autoComplete="new-password"
                            disabled={changePassword.isPending}
                            required
                        />
                    </span>
                    {errors.repeat && <span className="client-form__error">{errors.repeat}</span>}
                </label>
            </div>

            <div className="client-form__actions">
                <button
                    type="submit"
                    className="client-form__submit"
                    disabled={changePassword.isPending}
                >
                    {changePassword.isPending ? 'Cambiando...' : 'Cambiar contraseña'}
                </button>
            </div>
        </form>
    );
}
